/**
 * Central analytics manager for tracking events across the app
 * Uses Firebase Analytics as the primary analytics platform
 * All events follow v2 schema as defined in ANALYTICS_EVENTS.md
 * Matches iOS AnalyticsManager.swift exactly
 */

import { logEvent, setUserId, setUserProperties } from 'firebase/analytics'
import { analytics } from '../firebase'
import { getSessionId, clearSessionId } from './session'
import { AnalyticsEvent } from './AnalyticsEvents'
import { AnalyticsParameters } from './AnalyticsParameters'
import { AnalyticsUserProperties, UserPropertyHelper } from './AnalyticsUserProperties'
import { ScreenName, ButtonName, ButtonLocation, ListName } from './ScreenName'
import store from '../store'
import { UserLoginData } from '../services/user'

// Get app version from package.json at build time
// Using a constant value that matches package.json version
// This can be updated via webpack DefinePlugin if needed
const APP_VERSION = '24.1.0'

/**
 * Central analytics manager singleton
 */
class AnalyticsManager {
  private static _instance: AnalyticsManager
  private readonly platform = 'web'
  private readonly appVersion: string = APP_VERSION

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance
   */
  public static get shared(): AnalyticsManager {
    if (!AnalyticsManager._instance) {
      AnalyticsManager._instance = new AnalyticsManager()
    }
    return AnalyticsManager._instance
  }

  /**
   * Initialize analytics manager
   * Should be called in App initialization
   */
  public initialize(): void {
    // Firebase Analytics is already initialized via firebase/index.ts
    // Set user properties if user is already logged in
    this.updateUserProperties()

    // Check if we're in development mode (safely)
    if (
      typeof process !== 'undefined' &&
      process.env?.NODE_ENV !== 'production'
    ) {
      console.log('📊 AnalyticsManager initialized')
    }
  }

  /**
   * Get standard parameters that should be included in all events
   */
  private getStandardParameters(): Record<string, any> {
    const params: Record<string, any> = {
      [AnalyticsParameters.platform]: this.platform,
      [AnalyticsParameters.appVersion]: this.appVersion,
      [AnalyticsParameters.sessionId]: getSessionId(),
      [AnalyticsParameters.timestamp]: new Date().toISOString(),
      [AnalyticsParameters.eventVersion]: 'v2',
    }

    // Add user ID if available
    const userId =  UserPropertyHelper.getCurrentUserId()
    if (userId) {
      params[AnalyticsParameters.userId] = userId
    }

     // Add user ID if available
    const storeId = UserPropertyHelper.getStoreId()
    if (storeId) {
      params[AnalyticsParameters.storeId] = storeId
    }

    return params
  }

  /**
   * Track a custom event
   */
  public trackEvent(
    eventName: AnalyticsEvent,
    screenName?: ScreenName,
    parameters: Record<string, any> = {}
  ): void {
    const allParameters = this.getStandardParameters()

    if (screenName) {
      allParameters[AnalyticsParameters.screenName] = screenName
    }

    // Merge custom parameters
    for (const [key, value] of Object.entries(parameters)) {
      allParameters[key] = value
    }
    // Log event to Firebase Analytics
    // Convert enum to string for Firebase
    logEvent(analytics, eventName as string, allParameters)

    // Check if we're in development mode (safely)
    if (
      typeof process !== 'undefined' &&
      process.env?.NODE_ENV !== 'production'
    ) {
      console.log(
        `****************\n\nAnalytics Event: ${eventName}`,
        allParameters
      )
    }
  }

  /**
   * Track screen view
   */
  public trackScreenView(screenName: ScreenName, screenClass?: string): void {
    const parameters: Record<string, any> = {
      [AnalyticsParameters.screenName]: screenName,
    }

    if (screenClass) {
      parameters[AnalyticsParameters.screenClass] = screenClass
    }

    this.trackEvent(AnalyticsEvent.SCREEN_VIEW, screenName, parameters)
  }

  /**
   * Track button click (convenience method)
   */
  public trackButtonClick(
    buttonName: ButtonName,
    buttonLocation: ButtonLocation,
    screenName: ScreenName,
    parameters: Record<string, any> = {}
  ): void {
    const allParameters: Record<string, any> = {
      [AnalyticsParameters.buttonName]: buttonName,
      [AnalyticsParameters.buttonLocation]: buttonLocation,
      ...parameters,
    }

    this.trackEvent(AnalyticsEvent.ACTION_BUTTON_CLICK, screenName, allParameters)
  }

  /**
   * Track item selection (convenience method)
   */
  public trackSelectItem(
    listName: ListName,
    screenName: ScreenName,
    parameters: Record<string, any> = {}
  ): void {
    const allParameters: Record<string, any> = {
      [AnalyticsParameters.listName]: listName,
      ...parameters,
    }

    this.trackEvent(AnalyticsEvent.ACTION_ITEM_SELECT, screenName, allParameters)
  }

  /**
   * Track API error (convenience method)
   * API errors are tracked globally without screen context
   */
  public trackApiError(
    endPoint: string,
    errorCode?: string,
    errorMessage?: string,
    additionalParams: Record<string, any> = {}
  ): void {
    const parameters: Record<string, any> = {
      ...additionalParams,
      [AnalyticsParameters.apiEndpoint]: endPoint,
    }

    if (errorCode) {
      parameters[AnalyticsParameters.errorCode] = errorCode
    }

    if (errorMessage) {
      parameters[AnalyticsParameters.errorMessage] = errorMessage
    }

    // Track without screenName (pass undefined)
    this.trackEvent(AnalyticsEvent.ERROR_API_FAILURE, undefined, parameters)
  }

  public trackApiException(
    endPoint: string,
    errorCode?: string,
    errorMessage?: string,
    additionalParams: Record<string, any> = {}
  ): void {
    const parameters: Record<string, any> = {
      ...additionalParams,
      [AnalyticsParameters.apiEndpoint]: endPoint,
    }

    if (errorCode) {
      parameters[AnalyticsParameters.errorCode] = errorCode
    }

    if (errorMessage) {
      parameters[AnalyticsParameters.errorMessage] = errorMessage
    }

    // Track without screenName (pass undefined)
    this.trackEvent(AnalyticsEvent.ERROR_API_EXCEPTION, undefined, parameters)
  }

  /**
   * Track validation error (convenience method)
   */
  public trackValidationError(
    screenName: ScreenName,
    errorCode?: string,
    errorMessage?: string,
    additionalParams: Record<string, any> = {}
  ): void {
    const parameters: Record<string, any> = {
      ...additionalParams,
    }

    if (errorCode) {
      parameters[AnalyticsParameters.errorCode] = errorCode
    }

    if (errorMessage) {
      parameters[AnalyticsParameters.errorMessage] = errorMessage
    }

    this.trackEvent(AnalyticsEvent.ERROR_FORM_VALIDATION, screenName, parameters)
  }

  /**
   * Update user properties in Firebase Analytics
   * Uses setUserProperties() with object (web SDK way)
   */
  public updateUserProperties(): void {
    try {
      const userDataState = store.getState().userData
      const user: UserLoginData | null = userDataState?.data

      if (!user) {
        setUserId(analytics, null)
        return
      }

      // Set user ID
      const userId = UserPropertyHelper.getCurrentUserId()
      setUserId(analytics, userId)

      // Prepare user properties object
      const userProperties: Record<string, string> = {
        [AnalyticsUserProperties.platform]: this.platform,
        [AnalyticsUserProperties.subscriptionStatus]: UserPropertyHelper.getSubscriptionStatus(user),
        [AnalyticsUserProperties.subscriptionPlan]: UserPropertyHelper.getSubscriptionPlan(user),
        [AnalyticsUserProperties.userType]: UserPropertyHelper.getUserType(user),
        [AnalyticsUserProperties.isStaffUser]: user.staffUserId ? 'true' : 'false',
      }

      // Set user level
      if (user.staffUserId) {
        // For staff users, get user level from selected store
        // Note: userLevel may not be available in Store type, defaulting to OWNER
        // If userLevel is available in the API response, it should be added to Store type
        userProperties[AnalyticsUserProperties.userLevel] = 'OWNER' // Default, update when userLevel is available in Store type
      } else {
        userProperties[AnalyticsUserProperties.userLevel] = 'OWNER'
      }

      // Set store ID
      const storeId = UserPropertyHelper.getStoreId()
      if (storeId) {
        userProperties[AnalyticsUserProperties.storeId] = storeId
      }

      // Set boolean properties
      userProperties[AnalyticsUserProperties.hasGstin] = UserPropertyHelper.hasGstin(user) ? 'true' : 'false'
      userProperties[AnalyticsUserProperties.hasLogo] = UserPropertyHelper.hasLogo(user) ? 'true' : 'false'
      userProperties[AnalyticsUserProperties.hasSigns] = UserPropertyHelper.hasSigns(user) ? 'true' : 'false'

      // Set user properties using Firebase web SDK
      setUserProperties(analytics, userProperties)

      // Check if we're in development mode (safely)
      if (
        typeof process !== 'undefined' &&
        process.env?.NODE_ENV !== 'production'
      ) {
        console.log('📊 Updated User Properties:', {
          userId,
          ...userProperties,
        })
      }
    } catch (error) {
      console.error('Failed to update user properties:', error)
    }
  }

  /**
   * Clear user properties and reset analytics data
   * Note: Firebase web SDK doesn't have resetAnalyticsData, so we just clear user ID
   */
  public clearUserProperties(): void {
    setUserId(analytics, null)
    // Note: Firebase web SDK doesn't have resetAnalyticsData like iOS
    // We clear session ID on logout instead
    clearSessionId()

    // Check if we're in development mode (safely)
    if (
      typeof process !== 'undefined' &&
      process.env?.NODE_ENV !== 'production'
    ) {
      console.log('📊 Cleared User Properties and Analytics Data')
    }
  }

  /**
   * Track fetch document link
   */
  public trackFetchDocLink(
    screenName: ScreenName,
    entityType: string,
    entityId: number,
    success: boolean,
    error: string | null,
    parameters: Record<string, any> = {}
  ): void {
    const params: Record<string, any> = {
      [AnalyticsParameters.screenName]: screenName,
      [AnalyticsParameters.entityType]: entityType,
      [AnalyticsParameters.entityId]: entityId,
      [AnalyticsParameters.result]: success ? 'success' : 'fail',
      ...parameters,
    }

    if (error) {
      params[AnalyticsParameters.errorMessage] = error
    }

    this.trackEvent(AnalyticsEvent.FETCH_DOC_LINK, screenName, params)
  }

  /**
   * Track view document
   */
  public trackViewDocument(
    screenName: ScreenName,
    entityType: string,
    entityId: number,
    success: boolean,
    parameters: Record<string, any> = {}
  ): void {
    const params: Record<string, any> = {
      [AnalyticsParameters.screenName]: screenName,
      [AnalyticsParameters.entityType]: entityType,
      [AnalyticsParameters.entityId]: entityId,
      [AnalyticsParameters.result]: success ? 'success' : 'fail',
      ...parameters,
    }

    this.trackEvent(AnalyticsEvent.VIEW_DOCUMENT, screenName, params)
  }

  /**
   * Track list loaded
   */
  public trackListLoaded(
    loadTrigger: string,
    listName: ListName,
    screenName: ScreenName,
    success: boolean,
    itemCount: number,
    parameters: Record<string, any> = {}
  ): void {
    const params: Record<string, any> = {
      [AnalyticsParameters.loadTrigger]: loadTrigger,
      [AnalyticsParameters.listName]: listName,
      [AnalyticsParameters.screenName]: screenName,
      [AnalyticsParameters.result]: success ? 'success' : 'fail',
      [AnalyticsParameters.itemCount]: itemCount,
      ...parameters,
    }

    this.trackEvent(AnalyticsEvent.LIST_LOADED, screenName, params)
  }
}

export default AnalyticsManager
